import { useQuery } from '@apollo/client';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import FilterItem from '@/components/Filters/filterItem';
import { COUNTRIES } from '@/components/Filters/filterTypes';
import { DEFAULT_HIGHLIGHT_COLOR, OG_MAP_ASSET_COLORS } from '@/components/Map/Utils/colors';
import { MAP_SOURCE_IDS } from '@/components/Map/Utils/constants';
import { analytics, defaultDimensions, dimensions } from '@/utils/analytics';
import { getUserId } from '@/utils/auth';
import { ANALYTICS_EVENT_ACTIONS, ANALYTICS_EVENTS } from '@/utils/constants';
import { gtag } from '@/utils/gtag';
import { getLatLongFromPoint } from '@/utils/helpers';
import { rIC } from '@/utils/requestIdleCallbackShim';
import { usePortfolioBridgeStore } from '../PortfolioBridge';
import MapFilterContext from './filterContext';
import {
  BASINS_WITH_MAP_FILTER_QUERY,
  COMPANIES_WITH_MAP_FILTER_QUERY,
  DEVELOPMENT_STATUS_QUERY,
  MAP_SCREENING_QUERY,
  SHORE_STATUS_QUERY,
} from './operations';

const useStyles = makeStyles(theme => ({
  screening: {
    ...theme.palette.mapTab,
    ...theme.palette.scrollbar,
  },
  select: {
    margin: theme.spacing(1),
  },
  minMaxContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  minMax: {
    width: '45%',
  },
  reset: {
    ...theme.palette.button,
    flexShrink: 0,
    margin: '18px 8px',
  },
  text: {
    '& root': {
      boxShadow:
        '0 1px 5px 0 rgba(0, 0, 0, 0.2), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14)',
    },
    '& input': {
      backgroundColor: '#fafafa',
      fontSize: '14px',
      borderRadius: '5px',
      padding: '10px 4px 3px 4px !important',
      marginLeft: '4px',
    },
    '& label': {
      transform: 'translate(12px, 7px) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(12px, -2px) scale(0.75)',
      },
    },
  },
  root: {
    '&.MuiAutocomplete-root .MuiInputLabel-root': {
      paddingTop: '2px',
    },
    '&.MuiAutocomplete-root .MuiFilledInput-root > input': {
      padding: '24px 4px 10px',
    },

    '&.MuiAutocomplete-root .MuiInputBase-root .startAdornment': {
      marginTop: '20px',
    },
  },
  input: {
    '&.MuiFilledInput-root:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
    },
    '&.MuiFilledInput-root.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
    },
  },
  radio: {
    paddingRight: '3px',
    color: '#2a2e49',
    '&.Mui-checked': {
      color: '#2a2e49',
    },
  },
  formGroup: {
    padding: '10px 5px',
    rowGap: '0.5rem',
  },
  formControlRoot: {
    margin: theme.spacing(1),
  },
  formLabel: {
    marginRight: 0,
    textAlign: 'left',
    lineHeight: 1,
  },
}));

const useScreeningData = () => {
  const { hasScreeningFilters, variables } = useContext(MapFilterContext);

  const { loading, data } = useQuery(MAP_SCREENING_QUERY, {
    variables,
    skip: !hasScreeningFilters,
    fetchPolicy: 'cache-and-network',
  });

  // If skip is true, assign an empty array to data to reset map when filters are cleared
  const queryData = useMemo(
    () => (!hasScreeningFilters ? { mapScreening: [] } : data),
    [hasScreeningFilters, data],
  );

  return {
    data: queryData,
    loading,
  };
};

const usePortfolioBridge = ({ mapScreening }) => {
  const { filters, operatorOnly, opportunities } = useContext(MapFilterContext);
  const addEntities = usePortfolioBridgeStore.use.addEntities();
  const setPortfolioBridgeFilters = usePortfolioBridgeStore.use.setFilters();

  useEffect(() => {
    if (!mapScreening) return;

    const types = {
      asset: 'asset',
      block: 'block',
      lease: 'block',
      field: 'field',
    };

    const entities = [];

    for (const entity of mapScreening) {
      const entityType = entity.entityType?.toLowerCase();
      entities.push({
        type: types[entityType],
        id: entity.id,
        legacyId: entity.legacyId,
        country: {
          id: entity.country.id,
          isoCode: entity.country.isoCode,
          name: entity.country.name,
        },
        name: entity.displayName || entity.name,
      });
    }
    addEntities(entities);
  }, [mapScreening, addEntities]);

  useEffect(() => {
    setPortfolioBridgeFilters({
      countries: filters.country,
      companies: filters.company,
      basins: filters.basin,
      shoreStatuses: filters.shoreStatus,
      developmentStatuses: filters.developmentStatus,
      npvMin: filters.npvMin ? parseFloat(filters.npvMin) : null,
      npvMax: filters.npvMax ? parseFloat(filters.npvMax) : null,
      productionMin: filters.prodMin ? parseFloat(filters.prodMin) : null,
      productionMax: filters.prodMax ? parseFloat(filters.prodMax) : null,
      reservesMin: filters.reservesMin ? parseFloat(filters.reservesMin) : null,
      reservesMax: filters.reservesMax ? parseFloat(filters.reservesMax) : null,
      operator: operatorOnly,
      opportunities,
    });
  }, [filters, operatorOnly, opportunities, setPortfolioBridgeFilters]);
};

const useMapGetScreening = ({ data }) => {
  const [screeningFeatures, setScreeningFeatures] = useState([]);
  const [screeningPointFeatures, setScreeningPointFeatures] = useState([]);
  const { variables, setSecondaryLegendValues } = useContext(MapFilterContext);

  const chosenCompaniesReduced = useMemo(() => {
    return variables.companies.slice(0, 3).reduce((acc, company) => {
      acc[company.id] = company.name;
      return acc;
    }, {});
  }, [variables.companies]);

  useEffect(() => {
    if (!data) return;

    const features = [];
    const pointFeatures = [];
    const colors = [...OG_MAP_ASSET_COLORS];
    const companyLabelsAndColors = {};

    for (const entity of data.mapScreening || []) {
      if (!entity.sectionGeomCoordinates?.length) {
        continue;
      }

      let assetColor;

      const participations =
        entity.currentParticipations
          ?.filter(p => p?.company && chosenCompaniesReduced[p.company.id])
          ?.sort(p => chosenCompaniesReduced[p.company.id]) || [];

      if (participations?.length) {
        const participationsSorted = participations.sort((a, b) =>
          chosenCompaniesReduced[a.company.id].localeCompare(chosenCompaniesReduced[b.company.id]),
        );
        const idHash = participationsSorted.map(p => p.company.id).join('+');
        const companyNames = participationsSorted
          .map(p => {
            const name = chosenCompaniesReduced[p.company.id];
            return name.length > 16 ? `${name.slice(0, 15)}.` : name;
          })
          .join('+');

        if (companyLabelsAndColors[idHash]) {
          assetColor = companyLabelsAndColors[idHash].color;
        } else {
          const color = colors.shift();
          assetColor = color;
          companyLabelsAndColors[idHash] = {
            title: companyNames,
            color,
            shape: 'square',
          };
        }
      }

      const properties = {
        name: entity.displayName,
        id: entity.id,
        legacyId: entity.legacyId,
        countryIsoCode: entity.country.isoCode,
        country: entity.country.name,
        entityType: entity.entityType,
        color: assetColor || DEFAULT_HIGHLIGHT_COLOR,
      };
      features.push({
        type: 'Feature',
        id: entity.id,
        properties,
        geometry: {
          type: entity.multiPolygon ? 'MultiPolygon' : 'Polygon',
          coordinates: entity.sectionGeomCoordinates,
        },
      });
      pointFeatures.push({
        type: 'Feature',
        id: entity.id,
        properties,
        geometry: {
          type: 'Point',
          coordinates: getLatLongFromPoint(entity.geom),
        },
      });
    }

    if (variables.companies.length > 3) {
      companyLabelsAndColors.more = {
        title: 'Other',
        color: DEFAULT_HIGHLIGHT_COLOR,
      };
    }

    setSecondaryLegendValues(Object.values(companyLabelsAndColors));
    setScreeningFeatures(features);
    setScreeningPointFeatures(pointFeatures);
  }, [data, setSecondaryLegendValues, chosenCompaniesReduced, variables.companies.length]);

  return {
    screeningPointFeatures,
    screeningFeatures,
  };
};

const useUpdateMap = ({ map, data, loading }) => {
  const mapStyleLoaded = useRef(false);
  const { screeningFeatures, screeningPointFeatures } = useMapGetScreening({ data });

  const loadData = useCallback(
    (screeningFeatures, screeningPointFeatures) => {
      if (loading || !map?.getSource) return;

      const loadScreeningSource = () => {
        // add screening data
        const screeningSource = map?.getSource(MAP_SOURCE_IDS.screening);
        screeningSource?.setData({
          type: 'FeatureCollection',
          features: screeningFeatures,
        });
      };

      const loadScreeningClusterSource = () => {
        // add screening cluster data
        const screeningClusterSource = map?.getSource(MAP_SOURCE_IDS.screeningCluster);
        screeningClusterSource?.setData({
          type: 'FeatureCollection',
          features: screeningPointFeatures,
        });
      };

      rIC(loadScreeningSource, { timeout: 1200 });
      rIC(loadScreeningClusterSource, { timeout: 1400 });
    },
    [loading, map],
  );

  useEffect(() => {
    if (!map || mapStyleLoaded.current) {
      return;
    }

    const load = () => {
      loadData(screeningFeatures, screeningPointFeatures);
      mapStyleLoaded.current = true;
    };

    map.on('style.load', load);

    return () => {
      map.off('style.load', load);
    };
  }, [map, loadData, screeningFeatures, screeningPointFeatures]);

  useEffect(() => {
    loadData(screeningFeatures, screeningPointFeatures);
  }, [screeningFeatures, screeningPointFeatures, loadData]);
};

const RadioGroupScreening = () => {
  const classes = useStyles();

  const { opportunities, setOpportunities, showEaWells, setShowEaWells } =
    useContext(MapFilterContext);

  return (
    <FormControl className={classes.formGroup} component='fieldset'>
      <RadioGroup aria-label='m&aOpportunities' name='m&aOpportunities' value={opportunities}>
        <FormControlLabel
          className={classes.formLabel}
          value={true}
          onClick={e => e.preventDefault()}
          control={
            <Radio
              onClick={() => {
                if (!opportunities) {
                  gtag('event', ANALYTICS_EVENTS.event, {
                    event_category: 'Maps',
                    event_action: ANALYTICS_EVENT_ACTIONS.buttonClick,
                    event_label: 'view_ma_opportunities',
                    userId: getUserId(),
                    ...defaultDimensions,
                  });
                }
                setOpportunities(!opportunities);
              }}
              className={classes.radio}
            />
          }
          label='View Potential M&A Opportunities'
        />
      </RadioGroup>
      <RadioGroup aria-label='e&aWells' name='e&aWells' value={showEaWells}>
        <FormControlLabel
          className={classes.formLabel}
          value={true}
          onClick={e => e.preventDefault()}
          control={
            <Radio
              onClick={() => {
                if (!showEaWells) {
                  gtag('event', ANALYTICS_EVENTS.event, {
                    event_category: 'Maps',
                    event_action: ANALYTICS_EVENT_ACTIONS.buttonClick,
                    event_label: 'view_ea_wells',
                    userId: getUserId(),
                    ...defaultDimensions,
                  });
                }
                setShowEaWells(eaWells => !eaWells);
              }}
              className={classes.radio}
            />
          }
          label='View E&A Wells to Watch'
        />
      </RadioGroup>
    </FormControl>
  );
};

const OperatorSwitch = () => {
  const classes = useStyles();
  const { operatorOnly, toggleOperatorOnly } = useContext(MapFilterContext);

  return (
    <FormGroup row className={classes.formGroup}>
      <FormControlLabel
        control={
          <Switch
            color='secondary'
            checked={operatorOnly}
            onChange={toggleOperatorOnly}
            name='operatorOnly'
          />
        }
        label='Operators Only'
      />
    </FormGroup>
  );
};

const MinMaxFilter = ({ label, minValue, maxValue, onMinChange, onMaxChange }) => {
  const classes = useStyles();

  return (
    <FormControl className={classes.formControlRoot}>
      <Typography>{label}</Typography>
      <div className={classes.minMaxContainer}>
        <div className={classes.minMax}>
          <TextField
            className={classes.text}
            label='Min'
            type='number'
            variant='filled'
            fullWidth={true}
            onChange={onMinChange}
            value={minValue}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        </div>
        <div className={classes.minMax}>
          <TextField
            className={classes.text}
            label='Max'
            type='number'
            variant='filled'
            fullWidth={true}
            onChange={onMaxChange}
            value={maxValue}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />
        </div>
      </div>
    </FormControl>
  );
};

const ScreeningFilters = ({ handleFilter }) => {
  const [shoreStatuses, setShoreStatuses] = useState([]);

  const { filters, variables, propVariables } = useContext(MapFilterContext);

  const handleShoreStatusChange = useCallback(
    selectedItems => {
      // Select/Deselect the 'Pre-salt' option if 'Deepwater' is selected/deselected:
      const preSalt = shoreStatuses.find(item => item.name.toLowerCase() === 'pre-salt');
      const deepwater = shoreStatuses.find(item => item.name.toLowerCase() === 'deepwater');
      const preSaltIndex = selectedItems.indexOf(preSalt);
      const deepwaterIndex = selectedItems.indexOf(deepwater);
      if (deepwaterIndex > -1 && preSaltIndex === -1) {
        selectedItems.push(preSalt);
      } else if (deepwaterIndex === -1 && preSaltIndex > -1) {
        selectedItems.splice(preSaltIndex, 1);
      }
      handleFilter('shoreStatus', selectedItems);
    },
    [handleFilter, shoreStatuses],
  );

  return (
    <>
      <FilterItem
        context={`countries-og-screening-filters`}
        filter={{
          query: COUNTRIES.query,
          name: 'Country/Area',
          optionKey: 'displayName',
          runQueryAfterOnChange: false,
          queryVariable: true,
          multiple: true,
          menuItems: null,
        }}
        setFilter={value => handleFilter('country', value)}
        value={filters.country}
        disabled={false}
        inModal={false}
        propVariables={COUNTRIES.propVariables(variables)}
      />
      <FilterItem
        context={`basins-og-screening-filters`}
        filter={{
          query: BASINS_WITH_MAP_FILTER_QUERY,
          name: 'Basin',
          optionKey: 'name',
          runQueryAfterOnChange: false,
          queryVariable: true,
          multiple: true,
          menuItems: null,
        }}
        setFilter={value => handleFilter('basin', value)}
        value={filters.basin}
        disabled={false}
        inModal={false}
        propVariables={propVariables}
      />
      <FilterItem
        context={`companies-og-screening-filters`}
        filter={{
          query: COMPANIES_WITH_MAP_FILTER_QUERY,
          name: 'Company',
          optionKey: 'name',
          runQueryAfterOnChange: true,
          queryVariable: 'search',
          multiple: true,
          menuItems: null,
          enablePagination: true,
          responseCallback: res => res?.companies || [],
        }}
        setFilter={value => handleFilter('company', value)}
        value={filters.company}
        disabled={false}
        inModal={false}
        propVariables={propVariables}
      />
      <FilterItem
        context={`hydrocarbon-og-screening-filters`}
        filter={{
          query: null,
          name: 'Hydrocarbon',
          optionKey: 'name',
          runQueryAfterOnChange: false,
          queryVariable: null,
          multiple: true,
          menuItems: [{ name: 'Oil' }, { name: 'Gas' }],
        }}
        setFilter={value => handleFilter('hydrocarbonType', value)}
        value={filters.hydrocarbonType}
        disabled={false}
        inModal={false}
        propVariables={propVariables}
      />
      <FilterItem
        context={`companies-og-screening-filters`}
        filter={{
          query: SHORE_STATUS_QUERY,
          name: 'Shore Status',
          optionKey: 'name',
          runQueryAfterOnChange: false,
          queryVariable: null,
          multiple: true,
          menuItems: null,
          hideItem: item => item.name.toLowerCase() === 'pre-salt',
        }}
        setFilter={handleShoreStatusChange}
        value={filters.shoreStatus}
        disabled={false}
        inModal={false}
        propVariables={propVariables}
        onDataLoaded={({ shoreStatus }) => setShoreStatuses(shoreStatus)}
      />
      <FilterItem
        context={`development-status-og-screening-filters`}
        filter={{
          query: DEVELOPMENT_STATUS_QUERY,
          name: 'Development Status',
          optionKey: 'name',
          runQueryAfterOnChange: false,
          queryVariable: null,
          multiple: true,
          menuItems: null,
        }}
        setFilter={value => handleFilter('developmentStatus', value)}
        value={filters.developmentStatus}
        disabled={false}
        inModal={false}
        propVariables={propVariables}
      />
    </>
  );
};

const MapWrapper = memo(({ map }) => {
  const { loading, data } = useScreeningData();

  useUpdateMap({ map, data, loading });
  usePortfolioBridge({ mapScreening: data?.mapScreening || null });

  return null;
});

const FiltersWrapper = memo(({ map }) => {
  const classes = useStyles();
  const { filters, isFirstFilter, resetState, setFilters } = useContext(MapFilterContext);

  const updateMapPosition = useCallback(
    (key, value) => {
      if (!map?.flyTo) {
        return;
      }
      // if first screening filter zoom map
      if (isFirstFilter()) {
        if (key === 'country') {
          map.flyTo({
            center: getLatLongFromPoint(value[0].geom),
            zoom: 4,
          });
          return;
        }

        map.flyTo({
          center: [0, 0],
          zoom: 1.5,
        });
      }
    },
    [isFirstFilter, map],
  );

  const updateFilters = useCallback(
    (key, value) => {
      const newFilters = { ...filters, [key]: value };

      const singleFilterDimensions = {
        ...dimensions(newFilters, 'maps'),
        event_category: 'Maps',
        event_action: ANALYTICS_EVENT_ACTIONS.singleFilter,
        event_label: analytics({ [key]: value }),
        userId: getUserId(),
      };

      gtag('event', ANALYTICS_EVENTS.event, singleFilterDimensions);

      const filterDimensions = {
        ...dimensions(newFilters, 'maps'),
        event_category: 'Maps',
        event_action: ANALYTICS_EVENT_ACTIONS.filter,
        event_label: analytics(newFilters),
        userId: getUserId(),
      };

      gtag('event', ANALYTICS_EVENTS.event, filterDimensions);
      setFilters(newFilters);
    },

    [filters, setFilters],
  );

  const handleFilter = useCallback(
    (key, value) => {
      updateMapPosition(key, value);
      updateFilters(key, value);
    },
    [updateMapPosition, updateFilters],
  );

  const resetScreening = () => {
    resetState();
    map?.flyTo({
      center: [0, 0],
      zoom: 1.5,
    });
  };
  return (
    <>
      <Button variant='contained' className={classes.reset} onClick={resetScreening}>
        reset screening
      </Button>
      <Divider />
      <RadioGroupScreening />
      <Divider style={{ marginBottom: '9px' }} />
      <ScreeningFilters handleFilter={handleFilter} />
      <OperatorSwitch />
      <Divider style={{ margin: '10px 0 10px' }} />
      <MinMaxFilter
        label='Current Production (BOE/D)'
        minValue={filters.prodMin}
        maxValue={filters.prodMax}
        onMinChange={e => handleFilter('prodMin', e.currentTarget.value)}
        onMaxChange={e => handleFilter('prodMax', e.currentTarget.value)}
      />
      <MinMaxFilter
        label='NPV (US$MM)'
        minValue={filters.npvMin}
        maxValue={filters.npvMax}
        onMinChange={e => handleFilter('npvMin', e.currentTarget.value)}
        onMaxChange={e => handleFilter('npvMax', e.currentTarget.value)}
      />
      <MinMaxFilter
        label='Reserves (MMBOE)'
        minValue={filters.reservesMin}
        maxValue={filters.reservesMax}
        onMinChange={e => handleFilter('reservesMin', e.currentTarget.value)}
        onMaxChange={e => handleFilter('reservesMax', e.currentTarget.value)}
      />
    </>
  );
});

const Screening = memo(({ map, hidden }) => {
  const classes = useStyles();

  if (hidden) {
    return null;
  }

  return (
    <div className={classes.screening}>
      <FiltersWrapper map={map} />
      <MapWrapper map={map} />
    </div>
  );
});

export default Screening;
